import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export interface Scope {
  tenantId: string;              // L0: Enterprise
  operatingGroupId?: string;     // L1 (optional)
  operatingEntityId: string;     // L2: Operating Entity
  operatingEntityName?: string;   // L2: human-readable entity name
  facilityId: string;           // L3: Facility
  subZoneId?: string;           // L4 (optional)
  name?: string;                // human-readable facility / scope label
}

export interface ScopeContextType {
  activeScope: Scope | null;
  availableScopes: Scope[];
  setActiveScope: (scope: Scope) => void;
  invalidateScope: () => void;
}

const ScopeContext = createContext<ScopeContextType | undefined>(undefined);

interface ScopeProviderProps {
  children: ReactNode;
  initialScopes?: Scope[];
}

export function ScopeProvider({ children, initialScopes = [] }: ScopeProviderProps) {
  const [activeScope, setActiveScopeState] = useState<Scope | null>(null);
  const [availableScopes, setAvailableScopes] = useState<Scope[]>(initialScopes);

  useEffect(() => {
    setAvailableScopes(initialScopes);
    if (activeScope === null && initialScopes.length === 1) {
      setActiveScopeState(initialScopes[0]);
    }
  }, [initialScopes]);

  const setActiveScope = useCallback((scope: Scope) => {
    setActiveScopeState(scope);
    localStorage.setItem('nyos_active_scope', JSON.stringify(scope));
    // Broadcast scope change event across workspaces
    window.dispatchEvent(new CustomEvent('scope-changed', { detail: scope }));
  }, []);

  const invalidateScope = useCallback(() => {
    setActiveScopeState(null);
    localStorage.removeItem('nyos_active_scope');
    window.dispatchEvent(new CustomEvent('scope-invalidated'));
  }, []);

  return (
    <ScopeContext.Provider value={{ activeScope, availableScopes, setActiveScope, invalidateScope }}>
      {children}
    </ScopeContext.Provider>
  );
}

export function useScopeContext(): ScopeContextType {
  const context = useContext(ScopeContext);
  if (context === undefined) {
    throw new Error('useScopeContext must be used within a ScopeProvider');
  }
  return context;
}
